import {
  convertDateTimeToTimeString,
  isTimeValid,
} from '@dataGrid/gridUtilities';
import appConstant from "../../../../../../../appConstants";
import { DateTimeEditorWithLabel } from "../components/DateTimeEditor";
import { withStyles } from "@material-ui/core";
import FormSectionHeader from "../common/FormSectionHeader";
import { isValid as isDateTimeValid, format, addDays } from "date-fns";
import { getFieldError, getShipperPickupWindow } from "./utils";
import { getTerminalDateByOffset } from "../../../../utils/utils";
import { memo } from "react";
import {
  FORM_VALIDATION_MSG,
  MAX_LENGTH_CONSTRAINTS,
  ZONE_OPTIONS,
} from "../constants";
import {
  isReadOnlyPickupViewMode,
  isCreatePickupMode,
  isEditOrMergePickupMode
} from '../utils';
import { TextFieldWithLabel } from "../components/TextField";
import { SelectWithLabel } from "../components/Select";
import { getDropdownOptionsList } from "../../../../../../common/utils";
const styles = (theme) => ({
  label: {
    minWidth: 100,
    marginTop: 8,
  },
  container: {
    display: "flex",
    flexWrap: "wrap",
    border: `1px solid ${theme.AppBGColor}`,
    borderRadius: "4px",
    padding: 8,
    justifyContent: "space-between",
  },
  input: {
    marginRight: 8,
  },
  fieldContainer: {
    alignItems: "flex-start",
  },
  routeLabel: {
    minWidth: 60,
    marginTop: 8,
  },
  zoneLabel: {
    minWidth: 60,
    marginTop: 8,
    alignSelf: "flex-start"
  },
  readOnlyLabel: {
    marginTop: 0,
  },
  warningText:{
    fontSize:12,
    color: theme.warningTextColorDispatch
  },
  route: {
    width: 100,
    margin: 0,
  },
  dispatchZone: {
    margin: "0 8 0 0",
    width: 103,
  },
  widerDispatchZone: {
    width: 200
  }
});
const PickupInfo = ({
  pickupInfo,
  pickupErrorInfo,
  updateFormData,
  mode,
  classes,
  pickupData,
  selectedTerminalOffset,
  shipperInfo,
  updateSpecialInstructions,
  configJSON,
  routeMappingData,
  isReadOnlyPickupMergeMode
}) => {
  const isReadOnly = isReadOnlyPickupViewMode(mode) || isReadOnlyPickupMergeMode;
  const handleTimeChange = (name) => (value) => {
    if (!value || isTimeValid(convertDateTimeToTimeString(value))) {
      const readyTime = name === "readyTime" ? value : pickupInfo.readyTime;
      const closeTime = name === "closeTime" ? value : pickupInfo.closeTime;
      const pickupDateTimeData = {
        ...pickupInfo,
        [name]: value,
      };
      const pickupDateTimeErrorData = {
        ...pickupErrorInfo,
        readyTime:
          name === "readyTime"
            ? getFieldError({ field: "readyTime", value: readyTime })
            : pickupErrorInfo.readyTime,
        closeTime:
          name === "closeTime" || pickupInfo.closeTime
            ? getFieldError({ field: "closeTime", value: closeTime, readyTime, mode })
            : pickupErrorInfo.closeTime,
      };
      updateFormData(
        "pickupDateTimeInfo",
        pickupDateTimeData,
        pickupDateTimeErrorData
      );
    }
  };

  const resetDateTimeError = (name) => {
    if (pickupErrorInfo[name]) {
      const pickupDateTimeErrorData = {
        ...pickupErrorInfo,
        [name]: null,
      };
      updateFormData("pickupDateTimeInfo", pickupInfo, pickupDateTimeErrorData);
    }
  };

  const handlePickupDate = (value) => {
    if ((!value || isDateTimeValid(value)) && pickupInfo.pickupDate !== value) {
      const pickupDateTimeData = {
        ...pickupInfo,
        pickupDate: value,
      };
      const pickupDateTimeErrorData = {
        ...pickupErrorInfo,
        pickupDate: getFieldError({
          field: "pickupDate",
          value,
          mode,
          terminalOffset: selectedTerminalOffset,
        }),
      };
      updateFormData(
        "pickupDateTimeInfo",
        pickupDateTimeData,
        pickupDateTimeErrorData
      );
      isCreatePickupMode(mode) && updateSpecialInstructions({
        specialInstructions: shipperInfo.shipperSpecialInstructions,
        pickupDate: value,
        pickupWindow: shipperInfo.pickupWindow,
      });
    }
  };

  const handleRouteChange = (value) => {
    const pickupDateTimeData = {
      ...pickupInfo,
      route: value,
    };
    updateFormData("pickupDateTimeInfo", pickupDateTimeData, pickupErrorInfo);
  };

  const handleZoneChange = (value) => {
    const dispatchZone = value?.value ?? null;
    const pickupInfoData = {
      ...pickupInfo,
      dispatchZone,
    };
    updateFormData("pickupDateTimeInfo", pickupInfoData, pickupErrorInfo);
  };

  const getZoneOptions = () => {
    let zoneNames = [];
    if (configJSON.populateZonesForPickups) {
      zoneNames = routeMappingData?.map((routeMapping) => routeMapping.primaryRouteName) ?? [];
    } else {
      zoneNames = ZONE_OPTIONS;
    }
    return getDropdownOptionsList(zoneNames);
  };

  const pickupDate = pickupInfo?.pickupDate
    ? format(pickupInfo?.pickupDate, appConstant.DATETYPES.MONTHDATEYEAR.format)
    : null;
  const terminalDate = new Date(
    getTerminalDateByOffset(new Date(), selectedTerminalOffset)
  );

  const isPickupDateReadOnly = isReadOnly || Boolean(pickupData?.driverId);

  const shipperPickupWindow = getShipperPickupWindow(
    new Date(pickupInfo.pickupDate),
    shipperInfo?.pickupWindow
  );
  const dispatchZoneInputClass = `${classes.dispatchZone} ${configJSON.populateZonesForPickups ? classes.widerDispatchZone : ""}`;
  return (
    <>
      <FormSectionHeader label="Pickup Info" />
      <div className={classes.container} id="pickupInfo">
        <TextFieldWithLabel
          label="Route"
          id="route"
          value={pickupInfo.route}
          className={classes.route}
          containerClass={isReadOnly ? "" : classes.fieldContainer}
          inputProps={{
            maxLength: MAX_LENGTH_CONSTRAINTS.CREATE_PICKUP_FORM.ROUTE,
          }}
          labelClass={`${classes.routeLabel} ${
            isReadOnly ? classes.readOnlyLabel : ""
          }`}
          isReadOnly={isReadOnly}
          handleChange={handleRouteChange}
        />
        <DateTimeEditorWithLabel
          id="readyTime"
          label="Pickup Ready"
          dateType={appConstant.DATETYPES.TIME}
          labelClass={`${classes.label} ${
            isReadOnly ? classes.readOnlyLabel : ""
          }`}
          inputClass={classes.input}
          containerClass={isReadOnly ? "" : classes.fieldContainer}
          value={convertDateTimeToTimeString(pickupInfo.readyTime)}
          onChange={handleTimeChange("readyTime")}
          disableFocusInput
          enablePickerPortal
          onKeyDown={() => resetDateTimeError("readyTime")}
          errorText={pickupErrorInfo.readyTime ?? ""}
          isReadOnly={isReadOnly}
        />
        <DateTimeEditorWithLabel
          id="closeTime"
          label="Close Time"
          dateType={appConstant.DATETYPES.TIME}
          labelClass={`${classes.label} ${
            isReadOnly ? classes.readOnlyLabel : ""
          }`}
          inputClass={classes.input}
          containerClass={isReadOnly ? "" : classes.fieldContainer}
          value={convertDateTimeToTimeString(pickupInfo.closeTime)}
          onChange={handleTimeChange("closeTime")}
          disableFocusInput
          enablePickerPortal
          errorText={pickupErrorInfo.closeTime ?? ""}
          onKeyDown={() => resetDateTimeError("closeTime")}
          isReadOnly={isReadOnly}
        />

        <DateTimeEditorWithLabel
          id="pickupDate"
          label="Pickup Date"
          labelClass={`${classes.label} ${
            isPickupDateReadOnly ? classes.readOnlyLabel : ""
          }`}
          containerClass={isPickupDateReadOnly ? "" : classes.fieldContainer}
          dateType={{
            ...appConstant.DATETYPES.MONTHDATEYEAR,
            updateFormat: "yyyy-MM-dd'T'00:00:00.000",
          }}
          minDate={terminalDate}
          maxDate={
            isEditOrMergePickupMode(mode)
              ? addDays(terminalDate, 7)
              : null
          }
          value={pickupDate}
          onChange={handlePickupDate}
          disableFocusInput
          enablePickerPortal
          onKeyDown={() => resetDateTimeError("pickupDate")}
          errorText={pickupErrorInfo.pickupDate ?? ""}
          minDateMessage={FORM_VALIDATION_MSG.MIN_PICKUP_DATE}
          maxDateMessage={
            isEditOrMergePickupMode(mode)
              ? FORM_VALIDATION_MSG.MAX_PICKUP_DATE
              : null
          }
          isReadOnly={isPickupDateReadOnly}
        />
        <SelectWithLabel
          id= "dispatchZone"
          label="Zone"
          data={getZoneOptions()}
          textFieldProps={{
            id: "dispatchZone",
            variant: "outlined",
          }}
          inputClass={dispatchZoneInputClass}
          onChange={handleZoneChange}
          value={pickupInfo.dispatchZone}
          labelClass={`${classes.zoneLabel} ${
            isReadOnly ? classes.readOnlyLabel : ""
          }`}
          isReadOnly={isReadOnly}
        />
      </div>
      {shipperPickupWindow.isClosed ? (
        <div className={classes.warningText}>
          {`${shipperInfo.shipperName} is closed on ${shipperPickupWindow.dayOfWeek}`}
        </div>
      ) : null}
    </>
  );
};

export default withStyles(styles)(memo(PickupInfo));
